import * as React from 'react';
import { toast } from 'sonner';
import {
  ASSET_OWNERSHIP_DESCRIPTIONS,
  ASSET_OWNERSHIP_LABELS,
  ASSET_OWNERSHIPS,
  isCompanyProperty,
  type AssetOwnership,
} from '@cfl/assets-shared';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/cargofl-ui';
import { CategoryPicker, SuggestionPicker } from '@/components/pickers';
import {
  ConditionPicker,
  EMPTY_CONDITION,
  type ConditionValue,
} from '@/components/condition-picker';
import { PhotoPicker, uploadPendingPhotos } from '@/components/photo-picker';
import { ApiError } from '@/lib/api';
import { useSettings } from '@/lib/admin-api';
import {
  useAsset,
  useAssetSuggestions,
  useAssetCategories,
  useCreateAsset,
  useUpdateAsset,
  type AssetDetail,
} from '@/lib/assets-api';

/**
 * Register or edit an asset.
 *
 * There is no Asset Code field, on create or on edit: the code is minted by the
 * database sequence when the row is inserted. The form shows what the next one WILL
 * be, read from settings, so the number is not a surprise - but it is never editable,
 * because two assets sharing a sticker number is the one failure this whole system is
 * built to prevent.
 */
export function AssetFormDialog({
  open,
  onOpenChange,
  assetId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The asset to edit. An ID rather than the record itself, deliberately.
   *
   * The register list only holds an AssetSummary, which has no vendor, invoice
   * number, warranty start or notes. Handing that to this form would initialise those
   * four as empty and then SAVE them as empty - silently wiping them on any edit made
   * from the list. Loading the detail here means the form always has the whole record,
   * whichever screen opened it.
   */
  assetId?: string;
}) {
  const editing = assetId !== undefined;
  const { data: asset, isPending: loadingAsset } = useAsset(editing ? assetId : null);
  const { data: categories } = useAssetCategories();
  const { data: settings } = useSettings();
  const create = useCreateAsset();
  const update = useUpdateAsset(assetId ?? '');

  const [form, setForm] = React.useState(() => blankForm(asset));
  /**
   * Held in memory until the asset exists, then uploaded against its id.
   *
   * Not part of `form`, because it is not a field that round-trips: on an edit the
   * already-attached photos live on the server and are managed from the Documents panel
   * on the detail page. This is only ever the NEW ones.
   */
  const [photos, setPhotos] = React.useState<File[]>([]);
  // Brand and name suggestions, from what is already registered under the chosen type.
  const { data: suggestions } = useAssetSuggestions(
    form.categoryId || null,
    form.brand || undefined,
  );
  // Re-seeded when the record arrives, not only when the dialog opens - on an edit
  // the detail lands a moment after the dialog does.
  React.useEffect(() => {
    if (open) {
      setForm(blankForm(asset));
      setPhotos([]);
    }
  }, [open, asset]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.categoryId) {
      toast.error('Choose an asset type.');
      return;
    }

    const payload = {
      categoryId: form.categoryId,
      name: form.name.trim(),
      brand: form.brand.trim() || null,
      model: form.model.trim() || null,
      serial: form.serial.trim() || null,
      purchasedAt: form.purchasedAt || null,
      vendor: form.vendor.trim() || null,
      invoiceNo: form.invoiceNo.trim() || null,
      // Empty stays null - "price not recorded" is different from zero, and a bulk of
      // donated or transferred kit legitimately has no price.
      price: form.price.trim() === '' ? null : Number(form.price),
      warrantyStart: form.warrantyStart || null,
      warrantyEnd: form.warrantyEnd || null,
      ownership: form.ownership,
      location: form.location.trim() || null,
      notes: form.notes.trim() || null,

      // Blank stays null, like price: "not recorded" is a different answer from zero,
      // and most furniture has no RAM at all.
      ramGb: form.ramGb.trim() === '' ? null : Number(form.ramGb),
      cpu: form.cpu.trim() || null,
      storageGb: form.storageGb.trim() === '' ? null : Number(form.storageGb),

      knownFaults: form.condition.faults,
      conditionNotes: form.condition.notes.trim() || null,
      conditionChecked: form.condition.checked,
    };

    try {
      if (editing) {
        await update.mutateAsync(payload);
        toast.success(`${asset?.code ?? 'Asset'} updated.`);
        if (photos.length > 0 && assetId) await uploadPendingPhotos('asset', assetId, photos);
      } else {
        const created = await create.mutateAsync(payload);
        toast.success(`Registered as ${created.code}.`);
        // After the asset exists, because there was no id to attach to until now. It
        // never throws: the asset IS registered, and reporting that as a failure
        // because one photo did not upload would be a lie about what happened.
        if (photos.length > 0) await uploadPendingPhotos('asset', created.id, photos);
      }
      onOpenChange(false);
    } catch (error) {
      // fieldErrors carries per-input messages; showing the first is more useful than
      // "Some fields need attention", which names nothing the user can go and fix.
      const message =
        error instanceof ApiError
          ? (Object.values(error.fieldErrors ?? {})[0]?.[0] ?? error.message)
          : 'That could not be saved.';
      toast.error(message);
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? `Edit ${asset?.code ?? 'asset'}` : 'Register an asset'}
          </DialogTitle>
          <DialogDescription>
            {editing ? (
              // Ownership is editable here, and the code is not. Said outright, because
              // the obvious guess is that switching to employee-owned renumbers the
              // asset - and a sticker that no longer matches the register is worse
              // than a prefix that turned out to be wrong.
              'The asset code cannot change — it is on the sticker and in every spreadsheet. Changing ownership does not renumber it.'
            ) : settings ? (
              <>
                This will be registered as{' '}
                <span className="at-num font-medium text-foreground">
                  {isCompanyProperty(form.ownership)
                    ? settings.nextCodePreview
                    : settings.nextEmployeeCodePreview}
                </span>
                .
              </>
            ) : (
              'A code is assigned automatically when you save.'
            )}
          </DialogDescription>
        </DialogHeader>

        {editing && loadingAsset ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading the asset…</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Asset type" hint="The category — Laptop, Monitor, Chair.">
                <CategoryPicker
                  categories={categories}
                  value={form.categoryId}
                  onChange={(v) => set('categoryId', v)}
                  allowCreate
                />
              </Field>

              <Field
                label="Asset name"
                hint="The specific item — “Dell Latitude 5440”. Search what is registered, or type a new one."
              >
                {/* A company buys twenty of the same laptop, so this is the field that
                    repeats most - and typing it from memory each time is twenty chances
                    to spell it differently. */}
                <SuggestionPicker
                  noun="asset name"
                  suggestions={suggestions?.names}
                  value={form.name}
                  onChange={(v: string) => set('name', v)}
                  disabled={!form.categoryId}
                />
              </Field>

              <Field
                label="Brand"
                hint="Search what is already registered under this type, or type a new one."
              >
                <SuggestionPicker
                  noun="brand"
                  suggestions={suggestions?.brands}
                  value={form.brand}
                  onChange={(v: string) => set('brand', v)}
                  disabled={!form.categoryId}
                />
              </Field>

              <Field
                label="Model"
                hint={form.brand ? `Models registered under ${form.brand}.` : undefined}
              >
                {/* Narrowed by the brand above, because a model belongs to a make -
                    offering Lenovo's list while somebody registers a MacBook is worse
                    than offering nothing at all. */}
                <SuggestionPicker
                  noun="model"
                  suggestions={suggestions?.models}
                  value={form.model}
                  onChange={(v: string) => set('model', v)}
                  disabled={!form.categoryId}
                />
              </Field>

              <Field
                label="Serial number"
                hint="Must be unique. Leave blank for furniture and anything unserialised."
              >
                <Input value={form.serial} onChange={(e) => set('serial', e.target.value)} />
              </Field>

              <Field label="Purchase date">
                <Input
                  type="date"
                  value={form.purchasedAt}
                  onChange={(e) => set('purchasedAt', e.target.value)}
                />
              </Field>

              <Field label="Vendor">
                <Input value={form.vendor} onChange={(e) => set('vendor', e.target.value)} />
              </Field>

              <Field label="Invoice number">
                <Input value={form.invoiceNo} onChange={(e) => set('invoiceNo', e.target.value)} />
              </Field>

              <Field label="Purchase price">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                />
              </Field>

              {/* Sits beside the price on purpose: both answer "who paid for this",
                and the empty cell that used to be here was the natural home for it. */}
              <Field label="Ownership" hint={ASSET_OWNERSHIP_DESCRIPTIONS[form.ownership]}>
                <Select
                  value={form.ownership}
                  onValueChange={(v) => set('ownership', v as AssetOwnership)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_OWNERSHIPS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {ASSET_OWNERSHIP_LABELS[o]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Warranty start">
                <Input
                  type="date"
                  value={form.warrantyStart}
                  onChange={(e) => set('warrantyStart', e.target.value)}
                />
              </Field>

              <Field label="Warranty end">
                <Input
                  type="date"
                  value={form.warrantyEnd}
                  onChange={(e) => set('warrantyEnd', e.target.value)}
                />
              </Field>

              {/* Free text, offering whatever is already in use. Not narrowed by asset
                  type, unlike brand and model: a room holds laptops and chairs alike,
                  so filtering by type would hide every office with no laptop in it. */}
              <Field
                label="Location"
                hint="Where it physically is — “Pune office, 2nd floor”. Leave blank if you do not know yet."
              >
                <SuggestionPicker
                  noun="location"
                  suggestions={suggestions?.locations}
                  emptyText="No locations recorded yet — type the first"
                  value={form.location}
                  onChange={(v: string) => set('location', v)}
                />
              </Field>
            </div>

            {/* Specs. Three small fields rather than one "16 GB, i5, 512 GB SSD" line,
                because the question they get asked is comparative - "who is still on
                8 GB" decides who gets the next upgrade. */}
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="RAM (GB)" hint="Leave blank for anything without memory.">
                <Input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  className="at-num"
                  value={form.ramGb}
                  onChange={(e) => set('ramGb', e.target.value)}
                  placeholder="16"
                />
              </Field>
              <Field label="Processor">
                <Input
                  value={form.cpu}
                  onChange={(e) => set('cpu', e.target.value)}
                  placeholder="i5-1135G7"
                />
              </Field>
              <Field label="Storage (GB)">
                <Input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  className="at-num"
                  value={form.storageGb}
                  onChange={(e) => set('storageGb', e.target.value)}
                  placeholder="512"
                />
              </Field>
            </div>

            <ConditionPicker
              value={form.condition}
              onChange={(next) => set('condition', next)}
              hint="Recorded now, and copied onto every handover, so a repair reported months later can be judged against what was already wrong today. Leave it untouched if nobody has checked — that is stored as “not checked”, never as “nothing wrong”."
            />

            <PhotoPicker
              files={photos}
              onChange={setPhotos}
              label="Photos of the item"
              hint="Evidence of what it looks like today — the lid, the screen, the serial sticker. Attached once this is saved, and shown against every later handover."
            />

            <Field label="Notes">
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="Anything worth knowing about this particular item."
              />
            </Field>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : editing ? 'Save changes' : 'Register asset'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-[11px] leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  );
}

interface FormState {
  categoryId: string;
  name: string;
  ownership: AssetOwnership;
  brand: string;
  model: string;
  serial: string;
  purchasedAt: string;
  vendor: string;
  invoiceNo: string;
  price: string;
  warrantyStart: string;
  warrantyEnd: string;
  location: string;
  notes: string;
  ramGb: string;
  cpu: string;
  storageGb: string;
  condition: ConditionValue;
}

/** Every field is a string, including price and the dates: that is what an input
 *  gives back, and converting on the way out is one place rather than twelve. */
function blankForm(asset?: AssetDetail): FormState {
  return {
    categoryId: asset?.categoryId ?? '',
    name: asset?.name ?? '',
    // A new asset is the company's until someone says otherwise: that is what the
    // register is mostly for, and it is the answer that keeps the asset in the exit
    // chase rather than quietly out of it.
    ownership: asset?.ownership ?? 'COMPANY',
    brand: asset?.brand ?? '',
    model: asset?.model ?? '',
    serial: asset?.serial ?? '',
    purchasedAt: asset?.purchasedAt ?? '',
    vendor: asset?.vendor ?? '',
    invoiceNo: asset?.invoiceNo ?? '',
    price: asset?.price ?? '',
    warrantyStart: asset?.warrantyStart ?? '',
    warrantyEnd: asset?.warrantyEnd ?? '',
    location: asset?.location ?? '',
    notes: asset?.notes ?? '',
    ramGb: asset?.ramGb != null ? String(asset.ramGb) : '',
    cpu: asset?.cpu ?? '',
    storageGb: asset?.storageGb != null ? String(asset.storageGb) : '',
    // `checked` comes from the stored timestamp, not from the list being non-empty:
    // an asset checked and found clean must re-open as "Nothing wrong" ticked, and one
    // nobody ever looked at must re-open with nothing ticked at all.
    condition: asset
      ? {
          faults: asset.knownFaults,
          notes: asset.conditionNotes ?? '',
          checked: asset.conditionCheckedAt !== null,
        }
      : EMPTY_CONDITION,
  };
}
